'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { createSegmentedControl } from '@flipster/react/design-system/segmented-control';
import { SpecialButton } from '@flipster/react/design-system/special/button';
import { css } from '@flipster/styled-system/css';
import { flex, vstack } from '@flipster/styled-system/patterns';

import { MissionsTemplate } from '../../../../contentful/lucky-draw/missions';
import { Rewards } from '../../../../contentful/lucky-draw/rewards';
import { SpinMachine, useSpinMachine } from '../../../../contentful/lucky-draw/spin-machine';
import { luckyDrawBunja } from '../../../../contentful/lucky-draw/state';
import { TokenCard } from '../../../../contentful/lucky-draw/token-card';
import { registerCompetitionBunja } from '../../action';
import { EventPeriod } from '../../component/event-period';
import { SeparatorTitle } from '../../component/separator-title';
import { Rules } from '../rules';

const SegmentedControl = createSegmentedControl<string>();

export const LuckyDraw = () => {
  const { luckyDrawsLayout, luckyDrawsAtom, luckyDrawSlugs } = useBunja(luckyDrawBunja);
  const luckyDraws = useAtomValue(luckyDrawsAtom);
  const { registerCompetition } = useBunja(registerCompetitionBunja);
  const [selectedTab, setSelectedTab] = useState<string>(luckyDrawsLayout?.[0]?.slug ?? '');

  const { handleDraw, isRunning, disabled, ...machineProps } = useSpinMachine(selectedTab);

  if (!luckyDrawsLayout) return null;

  return (
    <div className={css({ w: '100%' })}>
      <SeparatorTitle
        colorSet='keyColor'
        title={luckyDrawsLayout?.[0]?.tokenTitle}
        subtitle={luckyDrawsLayout?.[0]?.tokenDescription}
        description={<EventPeriod start={luckyDraws?.[0]?.startDate} end={luckyDraws?.[0]?.endDate} />}
      />
      <ul
        className={flex({
          flexDirection: { base: 'column', tablet: 'row' },
          gap: { base: 16, tablet: 24 },
          justify: 'center',
          mt: 16,
          mb: 40,
        })}
      >
        {luckyDrawSlugs?.map((slug, index) => (
          <TokenCard
            index={index}
            key={slug}
            handleRegister={registerCompetition}
            css={{ bg: 'rgba(var(--key-color-rgb), 0.03)' }}
          />
        ))}
      </ul>
      <SeparatorTitle
        colorSet='normal'
        title={luckyDrawsLayout?.[0]?.title}
        description={luckyDrawsLayout?.[0]?.description}
      />
      {luckyDrawsLayout.length > 1 && (
        <SegmentedControl
          size='medium'
          value={selectedTab}
          onChange={setSelectedTab}
          css={{ w: 'fit-content', mx: 'auto', mt: 16 }}
        >
          {luckyDrawsLayout.map((item) => (
            <SegmentedControl.Item key={item.slug} value={item.slug ?? ''}>
              {item.tokenIcon?.title ?? ''}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
      )}
      <div className={vstack({ gap: { base: 20, tablet: 16 }, pt: { base: 20, tablet: 32 } })}>
        <SpinMachine isRunning={isRunning} {...machineProps} />
        <SpecialButton
          label={<FormattedMessage id='Campaign.r251124.Pick_your_reward' />}
          onClick={handleDraw}
          disabled={isRunning || disabled}
          css={{ mt: 16, minW: { base: '100%', tablet: 320 } }}
        />
      </div>
      <Rewards />
      <MissionsTemplate eventLocation='competition' showUpdatedTime />
      <Rules />
    </div>
  );
};
